/**
 * mergedListApi.js
 *
 * Generic engine for the recurring requirement "fetch two APIs, merge them on
 * a common key, show the result in the existing ListView". Nothing here knows
 * about workflows, candidates or any specific endpoint — each concrete
 * requirement is expressed as a small config passed to
 * createMergedListFetcher(), which returns a function matching ListView's
 * fetchData contract: (moduleName, limit, offset, options) => response.
 *
 * The merged response is emitted in the same envelope /module-data-list uses
 * ({ status, total, data: { data: rows } }), so ListView's default
 * responseMapper (mapListResponse) and the responseMappings JSON configs work
 * unchanged — never create new mapping JSON files for a merge.
 *
 * Two modes:
 *
 * 1. SERVER-PAGINATED (preferred — configure fetchPrimaryPage):
 *    Each ListView page change issues ONE primary request for that page
 *    (limit/offset forwarded to the server) and ONE secondary request scoped
 *    to the page's merge keys. Nothing is fetched beyond the current page.
 *    The primary response's server-side total drives pagination.
 *
 * 2. LEGACY FULL-SET (configure fetchPrimary):
 *    For primary endpoints with no pagination at all (e.g. a workflow stage's
 *    embedded data array). The full primary set is fetched and paginated
 *    client-side against a bulk secondary fetch. Do NOT use this for
 *    endpoints that support server paging.
 *
 * Concepts:
 *   - PRIMARY source: drives the list — its rows define membership, order and
 *     total. A primary row with no secondary match is KEPT and rendered with
 *     placeholders ('-') for the secondary-sourced columns; dropping it would
 *     desync the row count from the server total and break pagination.
 *   - SECONDARY source: enriches each primary row, matched on the common key.
 *     If the secondary request fails while the primary succeeds, the page
 *     still renders (unenriched) instead of erroring out. A primary failure
 *     always rejects — there is no list without the driver.
 *   - Merge keys are always compared as strings (String(value)), so a numeric
 *     id on one side matches its string form on the other.
 *   - Duplicate keys among SECONDARY rows: the first row wins. Duplicate keys
 *     among PRIMARY rows both merge against the same secondary record and
 *     both stay in the list (membership is the primary's business).
 *
 * Staleness: ListView already discards responses from superseded requests
 * (its effect-cleanup `cancelled` flag), so a slow older page can never
 * overwrite a newer one there. This engine additionally tracks its own call
 * sequence so a superseded call skips the expensive per-id fallback lookups.
 *
 * Example — see workflowModuleListApi.js for real instances of both modes.
 */

import { mapListResponse } from './responseMapping/mapListResponse';

// Keys tried, in order, to find the shared identifier on either side of a
// merge when no explicit key is configured.
const DEFAULT_MERGE_KEYS = [
  'referenceId', 'ReferenceId',
  'candidateId', 'CandidateId',
  'entityId', 'EntityId',
  'id', 'Id', '_id',
];

export function createKeyResolver(keys = DEFAULT_MERGE_KEYS) {
  return function resolveKey(record) {
    if (!record || typeof record !== 'object') return '';
    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && value !== '') return String(value);
    }
    return '';
  };
}

// Every merge-key value a record carries — not just the first. The two sides
// of a merge often name their shared id differently (workflow.entityId vs the
// module record's id/_id while the record ALSO has an unrelated referenceId),
// so indexing the secondary side under a single "best" key can miss matches
// the data actually supports.
function collectKeys(record, keys = DEFAULT_MERGE_KEYS) {
  if (!record || typeof record !== 'object') return [];
  const values = [];
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') values.push(String(value));
  }
  return values;
}

export const resolveMergeKey = createKeyResolver();

// Accepts a field name ('referenceId'), a priority list of field names, or a
// custom (record) => key function.
function normalizeKeyResolver(key) {
  if (typeof key === 'function') return key;
  if (typeof key === 'string') return createKeyResolver([key]);
  if (Array.isArray(key)) return createKeyResolver(key);
  return resolveMergeKey;
}

// Primary fields win over secondary fields on name collisions; the row id
// stays the secondary (module record) id so existing row navigation and
// detail routes keep working.
export function defaultMergeRow(primaryRow, secondaryRow, key) {
  if (!secondaryRow || typeof secondaryRow !== 'object') return primaryRow;
  return {
    ...secondaryRow,
    ...primaryRow,
    id: secondaryRow.id ?? primaryRow.id ?? key,
  };
}

// The same envelope /module-data-list responds with, so mapListResponse
// resolves rows via payload.data and total via response.total unchanged.
// `meta` carries the admin-configured list metadata (fields, tabs, actions…)
// from the module response so ListView renders the admin's column config
// instead of falling back to fields synthesized from row keys.
export function toModuleListResponse(rows, total, meta = {}) {
  return { status: 'success', total, data: { data: rows, ...meta } };
}

/**
 * Generic merge helper for two arbitrary APIs.
 *
 * Callers provide the two fetch functions, the arguments to call them with,
 * key resolvers, an item merger, and a final response combiner.
 */
export async function mergeApiResponses({
  fetchPrimary,
  fetchSecondary,
  primaryArgs = [],
  secondaryArgs = [],
  extractPrimaryItems = (response) => response,
  extractSecondaryItems = (response) => response,
  primaryKey,
  secondaryKey,
  mergeItem = defaultMergeRow,
  combineResponse = ({ mergedItems }) => mergedItems,
}) {
  const [primaryResponse, secondaryResponse] = await Promise.all([
    fetchPrimary(...(typeof primaryArgs === 'function' ? primaryArgs() : primaryArgs)),
    fetchSecondary(...(typeof secondaryArgs === 'function' ? secondaryArgs() : secondaryArgs)),
  ]);

  const primaryItems = extractPrimaryItems(primaryResponse) ?? [];
  const secondaryItems = extractSecondaryItems(secondaryResponse) ?? [];
  const resolvePrimary = normalizeKeyResolver(primaryKey);
  const resolveSecondary = normalizeKeyResolver(secondaryKey);

  const secondaryByKey = new Map();
  secondaryItems.forEach((item) => {
    const key = resolveSecondary(item);
    if (key && !secondaryByKey.has(key)) secondaryByKey.set(key, item);
  });

  const mergedItems = primaryItems.map((item) => {
    const key = resolvePrimary(item);
    return mergeItem(item, secondaryByKey.get(key), key);
  });

  return combineResponse({
    primaryResponse,
    secondaryResponse,
    primaryItems,
    secondaryItems,
    mergedItems,
  });
}

// Pull the admin-configured list metadata off a /module-data-list-shaped
// response (either flat or nested under `data`), matching the paths
// mapListResponse resolves. Keys with no value are omitted so the final
// mapping can still fall back (e.g. synthesize fields when none exist).
const META_KEYS = ['fields', 'fieldConfig', 'tabs', 'tabField', 'actionRules', 'actions', 'columnActions'];

export function extractListMeta(response) {
  const payload = response?.data ?? response;
  const meta = {};
  for (const key of META_KEYS) {
    const value = response?.[key] ?? payload?.[key];
    const isEmpty = value == null || (Array.isArray(value) && value.length === 0);
    if (!isEmpty) meta[key] = value;
  }
  return meta;
}

// 'clientRate', 'client_rate' and 'Client Rate' all normalize to 'clientrate'.
const normalizeFieldName = (name) => String(name ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

// Insert extraFields directly after the admin field named by `anchor` (string
// or array of candidate names, matched against value/label ignoring case and
// separators); append when no anchor is given or none matches.
//
// Position must survive ListView's getOrderedFields, which re-sorts by each
// field's numeric `order` when any field has one — so besides splicing, give
// the inserted fields fractional orders between the anchor's and the next
// field's (anchor 5 → 5.2, 5.4, ...). An extraField with its own explicit
// `order` is left alone so callers can still place columns manually.
function insertFieldsAfter(fields, extraFields, anchor) {
  const anchors = (Array.isArray(anchor) ? anchor : [anchor])
    .map(normalizeFieldName)
    .filter(Boolean);
  const index = anchors.length
    ? fields.findIndex((field) =>
        anchors.includes(normalizeFieldName(field?.value)) ||
        anchors.includes(normalizeFieldName(field?.label)))
    : -1;
  if (index === -1) return [...fields, ...extraFields];

  const anchorOrder = Number(fields[index]?.order);
  const positioned = Number.isFinite(anchorOrder)
    ? extraFields.map((field, i) =>
        field.order == null || field.order === ''
          ? { ...field, order: anchorOrder + (i + 1) / (extraFields.length + 1) }
          : field)
    : extraFields;
  return [...fields.slice(0, index + 1), ...positioned, ...fields.slice(index + 1)];
}

const mapRows = (response, moduleName) => mapListResponse(response, moduleName).rows;

// Default page extractor for server-paginated primaries: rows via
// mapListResponse, total from the response's server-side total.
const mapPage = (response, moduleName) => {
  const { rows, total } = mapListResponse(response, moduleName);
  return { rows, total };
};

/**
 * @param {object} config
 *
 * Server-paginated mode (preferred):
 * @param {Function} [config.fetchPrimaryPage]   async (moduleName, limit, offset, options) => response.
 *   ONE page of the driver list, paginated by the server. Configuring this
 *   selects paginated mode and fetchPrimary/fetchSecondary are ignored.
 * @param {Function} [config.extractPrimaryPage] (response, moduleName) => { rows, total }.
 *   Default: mapListResponse. `total` must be the server-side grand total —
 *   it drives ListView's pager.
 * @param {Function} [config.fetchSecondaryByKeys] async (moduleName, keys, options) => response.
 *   The secondary records for THIS page's merge keys (deduped strings).
 *   Called once per page, only when the page has keys.
 *
 * Legacy full-set mode:
 * @param {Function} [config.fetchPrimary]        async (moduleName, limit, offset, options) => response.
 *   Full driver set for endpoints with no server paging (paginated client-side here).
 * @param {Function} [config.extractPrimaryRows]  (response, moduleName) => rows. Default: mapListResponse.
 * @param {Function} [config.fetchSecondary]      async (moduleName, limit, offset, options) => response.
 * @param {number}   [config.secondaryFetchLimit=200] bulk secondary fetch size — a performance
 *   knob, not a correctness one, when fetchSecondaryById is provided.
 *
 * Shared:
 * @param {Function} [config.extractSecondaryRows] (response, moduleName) => rows. Default: mapListResponse.
 * @param {Function} [config.fetchSecondaryById]   async (moduleName, id) => record. Per-id fallback for
 *   primary rows the keyed/bulk secondary fetch didn't cover; omit to skip enrichment for those rows.
 *   The returned record is only used if it actually carries the requested key — a backend that
 *   ignores the id filter and returns an arbitrary record can't corrupt the merge.
 * @param {string|string[]|Function} [config.primaryKey]   key field(s)/resolver on primary rows.
 * @param {string|string[]|Function} [config.secondaryKey] key field(s)/resolver on secondary rows.
 * @param {Function} [config.mergeRow]           (primaryRow, secondaryRow, key) => mergedRow.
 * @param {object[]} [config.extraFields] static column configs (ListView field
 *   shape: { value, label, isVisible, ... }) appended after the admin-configured
 *   fields — for merged-in values that have no admin-side configuration.
 * @param {string|string[]} [config.extraFieldsAfter] admin field to insert the
 *   extraFields directly after, matched against each field's value/label
 *   ignoring case and separators (so 'clientRate' also matches 'client_rate'
 *   and 'Client Rate'). Falls back to appending when no field matches.
 * @returns {Function} ListView-compatible fetchData.
 */
export function createMergedListFetcher({
  fetchPrimaryPage,
  extractPrimaryPage = mapPage,
  fetchSecondaryByKeys,
  fetchPrimary,
  extractPrimaryRows = mapRows,
  fetchSecondary,
  extractSecondaryRows = mapRows,
  fetchSecondaryById,
  primaryKey,
  secondaryKey,
  mergeRow = defaultMergeRow,
  secondaryFetchLimit = 200,
  extraFields = [],
  extraFieldsAfter,
}) {
  const resolvePrimaryKey = normalizeKeyResolver(primaryKey);
  // A custom resolver function yields one key per row; field-name configs
  // (or the default priority list) index the row under every id it carries.
  const collectSecondaryKeys = typeof secondaryKey === 'function'
    ? (row) => {
        const key = secondaryKey(row);
        return key ? [String(key)] : [];
      }
    : (row) => collectKeys(
        row,
        typeof secondaryKey === 'string'
          ? [secondaryKey]
          : (Array.isArray(secondaryKey) ? secondaryKey : DEFAULT_MERGE_KEYS),
      );

  // Monotonic call sequence: a call superseded by a newer one skips its
  // per-id fallback lookups (ListView drops the stale result anyway).
  let callSeq = 0;

  function indexSecondaryRows(rows) {
    const byKey = new Map();
    rows.forEach((row) => {
      for (const key of collectSecondaryKeys(row)) {
        if (!byKey.has(key)) byKey.set(key, row); // duplicate secondary keys: first wins
      }
    });
    return byKey;
  }

  // Per-id fallback, verified: only trust the record if it carries the key we
  // asked for (guards against backends that ignore the id filter and return
  // an unrelated record).
  async function lookupSecondaryById(moduleName, key) {
    if (!fetchSecondaryById || !key) return null;
    try {
      const record = await fetchSecondaryById(moduleName, key);
      return record && collectSecondaryKeys(record).includes(String(key)) ? record : null;
    } catch (error) {
      console.error(`[mergedListApi] per-id secondary lookup failed for "${key}"`, error);
      return null;
    }
  }

  function warnOnNoMatch(pageRows, secondaryRows, secondaryByKey) {
    // Both sides returned rows but nothing on the page matches: almost always
    // a merge-key mismatch. Surface the compared values in dev instead of
    // letting the list silently render unenriched rows.
    if (import.meta.env?.DEV && secondaryRows.length && pageRows.length
        && !pageRows.some((row) => secondaryByKey.has(resolvePrimaryKey(row)))) {
      console.warn('[mergedListApi] no merge-key match between primary and secondary rows', {
        samplePrimaryKey: resolvePrimaryKey(pageRows[0]),
        sampleSecondaryKeys: collectSecondaryKeys(secondaryRows[0]),
        samplePrimaryRow: pageRows[0],
        sampleSecondaryRow: secondaryRows[0],
      });
    }
  }

  async function mergePage(pageRows, secondaryByKey, moduleName, isStale) {
    return Promise.all(
      pageRows.map(async (row) => {
        const key = resolvePrimaryKey(row);
        const match = secondaryByKey.get(key)
          ?? (isStale() ? null : await lookupSecondaryById(moduleName, key));
        return mergeRow(row, match, key);
      }),
    );
  }

  function buildResponse(mergedRows, total, responses) {
    // Either side may be the /module-data-list response that carries the
    // admin-configured list metadata — take each meta key from the first
    // response (primary, then secondary) that has it, so the merged list
    // honours the admin setup regardless of merge orientation.
    // Static extraFields (values with no admin-side config) go after them.
    const meta = {};
    for (const response of responses) {
      if (!response) continue;
      const extracted = extractListMeta(response);
      for (const key of Object.keys(extracted)) {
        if (meta[key] == null) meta[key] = extracted[key];
      }
    }
    if (extraFields.length) {
      meta.fields = insertFieldsAfter(meta.fields ?? [], extraFields, extraFieldsAfter);
    }
    return toModuleListResponse(mergedRows, total, meta);
  }

  // ── Server-paginated mode ──────────────────────────────────────────────────
  async function fetchPaginated(moduleName, limit, offset, options, isStale) {
    // Secondary is keyed by the page's ids, so it necessarily follows the
    // primary response: exactly two requests per page, sequential.
    const primaryResponse = await fetchPrimaryPage(moduleName, limit, offset, options);
    const page = extractPrimaryPage(primaryResponse, moduleName) ?? {};
    const pageRows = Array.isArray(page.rows) ? page.rows : [];
    const total = Number.isFinite(Number(page.total)) ? Number(page.total) : pageRows.length;

    // Out-of-range / empty page: keep the server total so the pager can
    // snap back to a valid page instead of collapsing to 0.
    const keys = [...new Set(pageRows.map(resolvePrimaryKey).filter(Boolean))];

    let secondaryResponse = null;
    let secondaryRows = [];
    if (keys.length && fetchSecondaryByKeys) {
      try {
        secondaryResponse = await fetchSecondaryByKeys(moduleName, keys, options);
        secondaryRows = extractSecondaryRows(secondaryResponse, moduleName) ?? [];
      } catch (error) {
        // Secondary failure must not blank the whole list — render the
        // primary rows unenriched (per-id fallback may still fill some in).
        console.error('[mergedListApi] secondary fetch failed — rendering unenriched page', error);
        secondaryResponse = null;
      }
    }

    const secondaryByKey = indexSecondaryRows(secondaryRows);
    warnOnNoMatch(pageRows, secondaryRows, secondaryByKey);
    const mergedRows = await mergePage(pageRows, secondaryByKey, moduleName, isStale);
    return buildResponse(mergedRows, total, [primaryResponse, secondaryResponse]);
  }

  // ── Legacy full-set mode ───────────────────────────────────────────────────
  async function fetchFullSet(moduleName, limit, offset, options, isStale) {
    const [primaryResult, secondaryResult] = await Promise.allSettled([
      fetchPrimary(moduleName, limit, offset, options),
      fetchSecondary
        ? fetchSecondary(moduleName, secondaryFetchLimit, 0, options)
        : Promise.resolve(null),
    ]);
    // No primary, no list — propagate. A failed secondary only costs enrichment.
    if (primaryResult.status === 'rejected') throw primaryResult.reason;
    const secondaryResponse = secondaryResult.status === 'fulfilled' ? secondaryResult.value : null;
    if (secondaryResult.status === 'rejected') {
      console.error('[mergedListApi] secondary fetch failed — rendering unenriched page', secondaryResult.reason);
    }

    const primaryRows = extractPrimaryRows(primaryResult.value, moduleName) ?? [];
    const secondaryRows = secondaryResponse
      ? (extractSecondaryRows(secondaryResponse, moduleName) ?? [])
      : [];

    const secondaryByKey = indexSecondaryRows(secondaryRows);
    const total = primaryRows.length;
    const pageRows = primaryRows.slice(offset, offset + limit);

    warnOnNoMatch(pageRows, secondaryRows, secondaryByKey);
    const mergedRows = await mergePage(pageRows, secondaryByKey, moduleName, isStale);
    return buildResponse(mergedRows, total, [primaryResult.value, secondaryResponse]);
  }

  return async function fetchMergedList(moduleName, limit = 10, offset = 0, options = {}) {
    const seq = ++callSeq;
    const isStale = () => seq !== callSeq;

    return fetchPrimaryPage
      ? fetchPaginated(moduleName, limit, offset, options, isStale)
      : fetchFullSet(moduleName, limit, offset, options, isStale);
  };
}
